<template>
  <div class="wallet-page">
    <Loader v-if="loading" />

    <div v-else class="wallet-page__container">
      <header class="wallet-page__header">
        <h1 class="wallet-page__title">Wallet</h1>
      </header>

      <!-- Unified Wallet Card -->
      <div class="wallet-card">
        <!-- Currency Switcher -->
        <div class="wallet-card__currency-tabs">
          <button
            v-for="wallet in walletBalance"
            :key="wallet.id"
            class="wallet-card__currency-tab"
            :class="{ 'wallet-card__currency-tab--active': selectedCurrency === wallet.currency }"
            @click="selectCurrency(wallet.currency)"
          >
            <CurrencyFlag :currency="wallet.currency" />
            <span>{{ getCurrencySymbol(wallet.currency) }}</span>
          </button>
        </div>

        <!-- Balance Display -->
        <div class="wallet-card__balance">
          <p class="wallet-card__balance-label">Available Balance</p>
          <h2 class="wallet-card__balance-amount">
            {{ formatAmountToDollar(selectedWallet?.balance || 0, 2, selectedCurrency) }}
          </h2>
        </div>

        <!-- Action Buttons -->
        <div class="wallet-card__actions">
          <button
            class="wallet-card__action-btn"
            :disabled="!canFund"
            @click="openFundModal"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>Fund</span>
          </button>
          <button
            class="wallet-card__action-btn"
            :disabled="!canWithdraw"
            @click="openWithdrawModal"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4V16M10 16L6 12M10 16L14 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Withdraw</span>
          </button>
          <button
            class="wallet-card__action-btn"
            :disabled="!canConvert"
            @click="openConvertModal"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 7H16M16 7L13 4M16 7L13 10M16 13H4M4 13L7 10M4 13L7 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Convert</span>
          </button>
        </div>
      </div>

      <!-- Portfolio Section - Commented out
      <div class="wallet-page__portfolio">
        <div class="wallet-page__stats">
          <div class="stat-card">
            <p class="stat-card__label">Total Shares</p>
            <h3 class="stat-card__value">{{ totalShares }}</h3>
          </div>
          <div class="stat-card">
            <p class="stat-card__label">Portfolio Value</p>
            <h3 class="stat-card__value">{{ formatAmountToDollar(portfolioValue, 2, selectedCurrency) }}</h3>
          </div>
          <div class="stat-card">
            <p class="stat-card__label">Holdings</p>
            <h3 class="stat-card__value">{{ holdingsCount }}</h3>
          </div>
        </div>
      </div>
      -->

      <div class="wallet-page__content">
        <TableHeader
          v-if="products.includes('INVEST')"
          :options="options"
          label="options"
          :tab="currentTab"
          @emitOption="switchTab"
        />
        <TransactionTable
          v-if="currentTab === 'Transactions'"
          table="transactions"
        />
        <TokensTable v-else-if="currentTab === 'Shares'" table="myTokens" />
      </div>

      <!-- Modals -->
      <FundModal 
        v-if="showFundModal" 
        :currency="selectedCurrency"
        @close-modal="closeFundModal" 
      />
      <WithdrawModal
        v-if="showWithdrawModal"
        :currency="selectedCurrency"
        :balance="selectedWallet?.balance || 0"
        @close-modal="closeWithdrawModal"
      />
      <ConvertModal
        v-if="showConvertModal"
        :fromCurrency="selectedCurrency"
        :balance="selectedWallet?.balance || 0"
        :wallets="walletBalance"
        @close-modal="closeConvertModal"
      />
    </div>
  </div>
</template>

<script>
import EmptyState from "@/components/notification/EmptyState";
import TableHeader from "@/components/tables/TableHeader";
import TokensTable from "@/components/tables/TokensTable";
import TransactionTable from "@/components/tables/TransactionTable";
import CurrencyFlag from "@/components/wallet/CurrencyFlag.vue";
import FundModal from "@/components/wallet/fund/FundModal.vue";
import ConvertModal from "@/components/wallet/convert/ConvertModal.vue";
import WithdrawModal from "@/components/wallet/withdraw/WithdrawModal";
import { useFundNotification } from "@/composables/notifyUserFunding";
import addBankApi from "@/utils/addBankApi";
import api from "@/utils/api.js";
import { formatAmountToDollar, isNumber, getCurrencySymbol } from "@/utils/helpers.js";
import responseHandler from "@/utils/responseHandler.js";
import { getItem, removeItem } from "@/utils/storage.js";
import { mapActions, mapGetters, mapState } from "vuex";
import { permissionsService } from "@/utils/permissionChecker.js";

export default {
  name: "Wallet",

  components: {
    WithdrawModal,
    FundModal,
    ConvertModal,
    CurrencyFlag,
    TableHeader,
    TransactionTable,
    TokensTable,
    EmptyState,
  },

  setup() {
    const { checkFundingStatus } = useFundNotification();
    return { checkFundingStatus };
  },

  data() {
    return {
      loading: false,
      showWithdrawModal: false,
      showFundModal: false,
      showConvertModal: false,
      selectedCurrency: "NGN",
      currentTab: "Transactions",
      totalShares: 0,
      portfolioValue: 0,
      holdingsCount: 0,
    };
  },

  computed: {
    ...mapState({
      minimumWithdrawal: (state) => state.organisationModule.minimumWithdrawal,
      entityCard: (state) => state.organisationModule.entityCard,
      bankDetails: (state) => state.Account.bankAccounts,
      currency: (state) => state.organisationModule.currency,
    }),
    ...mapGetters("organisationModule", ["products", "walletBalance"]),
    
    selectedWallet() {
      return this.walletBalance.find(w => w.currency === this.selectedCurrency);
    },
    
    canFund() {
      return this.canPerformAction('wallet:*') || this.canPerformAction('wallet:fundWallet');
    },
    
    canWithdraw() {
      const hasPermission = this.canPerformAction('wallet:*') || this.canPerformAction('wallet:withdrawWallet');
      return hasPermission && (this.selectedWallet?.balance || 0) > 0;
    },
    
    canConvert() {
      return (this.selectedWallet?.balance || 0) > 0 && this.walletBalance.length > 1;
    },
    
    options() {
      const hasInvest = this.products.includes("INVEST");
      return hasInvest ? ["Transactions", "Shares"] : ["Transactions"];
    },
  },

  created() {
    this.getWithdrawalDetails();
    this.initializePermissions();
  },

  watch: {
    walletBalance: {
      immediate: true,
      handler(wallets) {
        if (wallets?.length && !this.selectedCurrency) {
          this.selectedCurrency = wallets[0].currency;
        }
      }
    }
  },

  mounted() {
    removeItem("fundPayload");
    const query = this.$route.query;
    if (query.ref) {
      this.finalizeBankConnection("nordigen", { code: query.ref });
      return;
    }
    if (query.code && query.state) {
      const redirectUri = `${window.location.origin}/dashboard/wallet`;
      const verifier = getItem(query.state);
      this.finalizeBankConnection("stitch", { verifier, code: query.code, redirectUri });
      return;
    }
    this.checkFundingStatus();
  },

  methods: {
    ...mapActions("organisationModule", [
      "getWalletBalance",
      "generateVirtualAccount",
      "getEntityCard",
      "setCurrency",
    ]),
    ...mapActions("Account", ["fetchBankAccounts"]),
    ...mapActions(["showToast", "setLoading"]),

    formatAmountToDollar,
    isNumber,
    getCurrencySymbol,

    selectCurrency(currency) {
      this.selectedCurrency = currency;
      this.setCurrency(currency);
    },

    openFundModal() {
      this.setCurrency(this.selectedCurrency);
      this.showFundModal = true;
    },

    openWithdrawModal() {
      this.setCurrency(this.selectedCurrency);
      this.showWithdrawModal = true;
    },

    openConvertModal() {
      this.setCurrency(this.selectedCurrency);
      this.showConvertModal = true;
    },

    closeFundModal() {
      this.showFundModal = false;
      removeItem("fundPayload");
      this.getWalletBalance();
    },

    closeWithdrawModal() {
      this.showWithdrawModal = false;
      removeItem("withdrawalPayload");
      this.getWalletBalance();
    },

    closeConvertModal() {
      this.showConvertModal = false;
      this.getWalletBalance();
    },

    switchTab(tab) {
      this.currentTab = tab;
    },

    canPerformAction(action) {
      return permissionsService.hasPermission(action);
    },

    initializePermissions() {
      permissionsService.loadUserPermissions();
    },

    async getWithdrawalDetails() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchBankAccounts(),
          this.getWalletBalance(),
          this.getEntityCard(),
        ]);
        this.loading = false;
      } catch (error) {
        responseHandler.handleError(error);
        this.loading = false;
      }
    },

    async finalizeBankConnection(type, data) {
      this.setLoading(true);
      this.showWithdrawModal = true;
      setTimeout(() => this.$router.push({ name: "Wallet" }), 100);
      try {
        const response = await addBankApi.finalizeBankConnection(type, data);
        await this.fetchBankAccounts();
        this.showToast({
          description: response.data.message,
          display: true,
          type: "success",
        });
        this.$router.push({ name: "Wallet" });
        this.setLoading(false);
      } catch (error) {
        responseHandler.handleError(error);
        this.setLoading(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-page: #F8FAFC;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$accent-primary: #6366F1;
$accent-light: #EEF2FF;

.wallet-page {
  width: 100%;
  min-height: 100vh;
  background: $bg-page;

  &__container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 24px 16px;

    @media screen and (min-width: 1024px) {
      padding: 32px 24px;
    }
  }

  &__header {
    margin-bottom: 24px;
  }

  &__title {
    font-size: 20px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }

  &__portfolio {
    margin-bottom: 24px;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  &__content {
    background: $bg-card;
    border: 1px solid $border-color;
    border-radius: 10px;
    padding: 20px;
  }
}

.wallet-card {
  background: $btn-primary;
  border: none;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;

  &__currency-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    overflow-x: auto;
    padding-bottom: 4px;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.3);
      border-radius: 2px;
    }
  }

  &__currency-tab {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
      color: #FFFFFF;
    }

    &--active {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.4);
      color: #FFFFFF;
    }
  }

  &__balance {
    text-align: center;
    padding: 32px 0;
    margin-bottom: 24px;
  }

  &__balance-label {
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.6);
    margin: 0 0 8px;
  }

  &__balance-amount {
    font-size: 32px;
    font-weight: 600;
    color: #FFFFFF;
    margin: 0;

    @media screen and (min-width: 768px) {
      font-size: 40px;
    }
  }

  &__actions {
    display: flex;
    justify-content: center;
    gap: 16px;

    @media screen and (min-width: 768px) {
      gap: 24px;
    }
  }

  &__action-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 24px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    color: #FFFFFF;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 80px;

    @media screen and (min-width: 768px) {
      min-width: 100px;
      padding: 16px 32px;
    }

    svg {
      color: #FFFFFF;
    }

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.2);
      color: #FFFFFF;

      svg {
        color: #FFFFFF;
      }
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      background: rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.5);

      svg {
        color: rgba(255, 255, 255, 0.5);
      }
    }
  }
}

.stat-card {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 16px 20px;

  &__label {
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: $text-muted;
    margin: 0 0 8px;
  }

  &__value {
    font-size: 18px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }
}
</style>
